import { useState } from "react";

import {
  deriveNotificationReadiness,
  type NotificationPermissionState,
} from "./notification-readiness";

// Notification preferences (GW-WEB-NOTIFICATION-PREFERENCES) - dark behind web_notifications.
// The ONE permission prompt fires only from the explicit button below, never at render/mount.
// No Push API, no subscription flow, no endpoint/key material in this slice - registration is
// a later, separately-assigned lane; until then granted-permission copy stays honest.

export function NotificationPreferences({
  flagEnabled,
  apiSupported,
  initialPermission,
  requestPermission,
}: {
  flagEnabled: boolean;
  apiSupported: boolean;
  initialPermission: NotificationPermissionState;
  /** Injected prompt action; production callers pass () => Notification.requestPermission(). */
  requestPermission: () => Promise<string>;
}) {
  const [permission, setPermission] = useState<NotificationPermissionState>(initialPermission);
  const [asking, setAsking] = useState(false);
  const state = deriveNotificationReadiness({ flagEnabled, apiSupported, permission });

  if (state.kind === "hidden") {
    return null;
  }

  async function onEnable() {
    setAsking(true);
    try {
      const result = await requestPermission();
      setPermission(result === "granted" ? "granted" : result === "denied" ? "denied" : "default");
    } catch {
      // The browser refused to even prompt - treat as unsupported-leaning denial, honestly.
      setPermission("denied");
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className="notifprefs" role="group" aria-label="Notification preferences">
      <h3 className="notifprefs__title">Web notifications</h3>
      <p className={`notifprefs__copy notifprefs__copy--${state.tone}`}>{state.copy}</p>
      {state.showEnableAction ? (
        <button
          type="button"
          className="notifprefs__enable"
          disabled={asking}
          onClick={() => void onEnable()}
        >
          Enable notifications
        </button>
      ) : null}
    </div>
  );
}
